import React from 'react';
import { StarIcon, HeartIcon, BriefcaseIcon, TrendingUpIcon, UserIcon } from './Icons';
import { useClient } from '../contexts/ClientContext';

interface HeroProps {
    onNavigate: (page: string, category?: string) => void;
    onOpenPremium: () => void;
    onOpenProfile?: () => void;
}

export const Hero: React.FC<HeroProps> = ({ onNavigate, onOpenPremium, onOpenProfile }) => {
  const { client, openAuthModal } = useClient();

  const handleClientAction = () => {
    if (client) {
      if (onOpenProfile) onOpenProfile();
      else openAuthModal('login');
    } else {
      openAuthModal('login');
    }
  };

  return (
    <section className="relative overflow-hidden min-h-[70vh] flex items-center justify-center bg-transparent pt-6 sm:pt-8 pb-8 sm:pb-12">
      {/* Reflets d'ambiance cosmique */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-64 h-64 bg-purple-600/10 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-600/10 rounded-full blur-[100px]"></div>
      </div>

      <div className="container relative z-10 px-3 sm:px-4 text-center max-w-7xl mx-auto">
        
        {/* Badge Cabinet */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-400 mb-5 backdrop-blur-sm shadow-sm">
            <StarIcon className="w-4 h-4 shrink-0 text-gold-400" />
            <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider">Le plus grand cabinet de voyance de France</span>
        </div>
        
        {/* Titre d'accroche */}
        <h2 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-4 sm:mb-6 drop-shadow-lg leading-tight tracking-tight">
          Plus de 100 Experts <br className="hidden xs:inline" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-gold-400"> à votre écoute</span>
        </h2>
        
        {/* Sous-titre */}
        <p className="text-sm sm:text-lg md:text-xl text-mystic-100 max-w-2xl mx-auto mb-6 sm:mb-8 leading-relaxed font-light px-2">
          Amour, travail, argent... Nos médiums purs, tarologues et astrologues vous répondent en direct sans attente.
        </p>

        {/* --- GRILLE UNIFIÉE DES 5 BOUTONS ALIGNÉS (MOBILE & DESKTOP PARFAIT) --- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5 sm:gap-3 lg:gap-2.5 xl:gap-3.5 max-w-7xl mx-auto mb-8 sm:mb-10">
            
            {/* 1. AMOUR & COUPLE */}
            <button 
                onClick={() => onNavigate('mediums', 'Amour')}
                className="group relative overflow-hidden specular-shine bg-gradient-to-br from-pink-950/80 via-purple-950/80 to-pink-900/80 backdrop-blur-md border border-pink-500/30 px-3 py-3 sm:px-3.5 sm:py-3.5 lg:px-2.5 lg:py-3 xl:px-3.5 xl:py-3.5 rounded-2xl flex items-center gap-2.5 sm:gap-3 lg:gap-2 xl:gap-3 hover:border-pink-400 hover:shadow-[0_0_25px_rgba(236,72,153,0.35)] transition-all transform hover:-translate-y-1 active:scale-[0.98] text-left cursor-pointer"
            >
                <div className="bg-pink-500/20 p-2 sm:p-2.5 lg:p-2 xl:p-2.5 rounded-xl group-hover:bg-pink-500 transition-colors shrink-0 border border-pink-400/20">
                    <HeartIcon className="w-5 h-5 sm:w-5 sm:h-5 text-pink-300 group-hover:text-white" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="text-white font-bold text-sm sm:text-base lg:text-[13px] xl:text-[14.5px] leading-tight mb-0.5 whitespace-nowrap tracking-tight">Amour & Couple</h3>
                    <p className="text-pink-200/90 text-xs lg:text-[10.5px] xl:text-xs whitespace-nowrap font-light">Avenir sentimental</p>
                </div>
            </button>

            {/* 2. TRAVAIL & CARRIÈRE */}
            <button 
                onClick={() => onNavigate('mediums', 'Travail')}
                className="group relative overflow-hidden specular-shine bg-gradient-to-br from-blue-950/80 via-indigo-950/80 to-blue-900/80 backdrop-blur-md border border-blue-500/30 px-3 py-3 sm:px-3.5 sm:py-3.5 lg:px-2.5 lg:py-3 xl:px-3.5 xl:py-3.5 rounded-2xl flex items-center gap-2.5 sm:gap-3 lg:gap-2 xl:gap-3 hover:border-blue-400 hover:shadow-[0_0_25px_rgba(59,130,246,0.35)] transition-all transform hover:-translate-y-1 active:scale-[0.98] text-left cursor-pointer"
            >
                <div className="bg-blue-500/20 p-2 sm:p-2.5 lg:p-2 xl:p-2.5 rounded-xl group-hover:bg-blue-500 transition-colors shrink-0 border border-blue-400/20">
                    <BriefcaseIcon className="w-5 h-5 sm:w-5 sm:h-5 text-blue-300 group-hover:text-white" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="text-white font-bold text-sm sm:text-base lg:text-[13px] xl:text-[14.5px] leading-tight mb-0.5 whitespace-nowrap tracking-tight">Travail & Carrière</h3>
                    <p className="text-blue-200/90 text-xs lg:text-[10.5px] xl:text-xs whitespace-nowrap font-light">Évolution & Projets</p>
                </div>
            </button>

            {/* 3. ARGENT & FINANCE */}
            <button 
                onClick={() => onNavigate('mediums', 'Finance')}
                className="group relative overflow-hidden specular-shine bg-gradient-to-br from-emerald-950/80 via-green-950/80 to-teal-900/80 backdrop-blur-md border border-emerald-500/30 px-3 py-3 sm:px-3.5 sm:py-3.5 lg:px-2.5 lg:py-3 xl:px-3.5 xl:py-3.5 rounded-2xl flex items-center gap-2.5 sm:gap-3 lg:gap-2 xl:gap-3 hover:border-emerald-400 hover:shadow-[0_0_25px_rgba(16,185,129,0.35)] transition-all transform hover:-translate-y-1 active:scale-[0.98] text-left cursor-pointer"
            >
                <div className="bg-emerald-500/20 p-2 sm:p-2.5 lg:p-2 xl:p-2.5 rounded-xl group-hover:bg-emerald-500 transition-colors shrink-0 border border-emerald-400/20">
                    <TrendingUpIcon className="w-5 h-5 sm:w-5 sm:h-5 text-emerald-300 group-hover:text-white" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="text-white font-bold text-sm sm:text-base lg:text-[13px] xl:text-[14.5px] leading-tight mb-0.5 whitespace-nowrap tracking-tight">Argent & Finance</h3>
                    <p className="text-emerald-200/90 text-xs lg:text-[10.5px] xl:text-xs whitespace-nowrap font-light">Opportunités & Gains</p>
                </div>
            </button>

            {/* 4. PACKS GOLD & PRESTIGE */}
            <button 
                onClick={() => onNavigate('tarifs')}
                className="group relative overflow-hidden specular-shine bg-gradient-to-br from-gold-500 via-amber-400 to-yellow-500 backdrop-blur-md border border-gold-300 px-3 py-3 sm:px-3.5 sm:py-3.5 lg:px-2.5 lg:py-3 xl:px-3.5 xl:py-3.5 rounded-2xl flex items-center gap-2.5 sm:gap-3 lg:gap-2 xl:gap-3 hover:shadow-[0_0_35px_rgba(251,191,36,0.6)] transition-all transform hover:-translate-y-1 active:scale-[0.98] text-left cursor-pointer"
            >
                <div className="absolute top-0 right-0 bg-red-600 text-white text-[8px] sm:text-[9px] font-black uppercase px-2 py-0.5 rounded-bl-lg shadow">
                    Forfaits Privés
                </div>
                <div className="bg-black/20 p-2 sm:p-2.5 lg:p-2 xl:p-2.5 rounded-xl shrink-0 border border-black/10">
                    <StarIcon className="w-5 h-5 sm:w-5 sm:h-5 text-black fill-current" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="text-black font-extrabold text-sm sm:text-base lg:text-[13px] xl:text-[14.5px] leading-tight mb-0.5 whitespace-nowrap tracking-tight">Packs & Forfaits</h3>
                    <p className="text-black/90 text-xs lg:text-[10.5px] xl:text-xs font-semibold whitespace-nowrap">50€ / 100€ Privilège</p>
                </div>
            </button>

            {/* 5. ESPACE CLIENT / CONNEXION */}
            <button 
                onClick={handleClientAction}
                className="group relative overflow-hidden specular-shine bg-gradient-to-br from-purple-900/90 via-indigo-900/90 to-purple-950/90 backdrop-blur-md border-2 border-purple-400/50 px-3 py-3 sm:px-3.5 sm:py-3.5 lg:px-2.5 lg:py-3 xl:px-3.5 xl:py-3.5 rounded-2xl flex items-center gap-2.5 sm:gap-3 lg:gap-2 xl:gap-3 hover:border-purple-300 hover:shadow-[0_0_30px_rgba(168,85,247,0.45)] transition-all transform hover:-translate-y-1 active:scale-[0.98] text-left ring-1 ring-purple-400/20 cursor-pointer"
            >
                {client && (
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[8px] sm:text-[9px] font-black uppercase px-2 py-0.5 rounded-bl-lg shadow">
                        En Ligne
                    </div>
                )}
                <div className="bg-purple-500/20 p-2 sm:p-2.5 lg:p-2 xl:p-2.5 rounded-xl group-hover:bg-purple-500 transition-colors shrink-0 border border-purple-400/30">
                    {client && client.avatar ? (
                        <img src={client.avatar} alt="avatar" className="w-5 h-5 sm:w-5 sm:h-5 rounded-full object-cover" />
                    ) : (
                        <UserIcon className="w-5 h-5 sm:w-5 sm:h-5 text-purple-300 group-hover:text-white" />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="text-white font-extrabold text-sm sm:text-base lg:text-[13px] xl:text-[14.5px] leading-tight mb-0.5 whitespace-nowrap tracking-tight">
                        {client ? client.name : "Espace Client"}
                    </h3>
                    <p className="text-purple-200/90 text-xs lg:text-[10.5px] xl:text-xs font-semibold whitespace-nowrap">
                        {client ? `Solde : ${client.consultationCreditsMinutes || 0} min` : "Connectez-vous"}
                    </p>
                </div>
            </button>
        </div>
        
        {/* Statistiques de confiance */}
        <div className="mt-4 sm:mt-6 flex flex-col items-center">
            <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-10 opacity-85">
                <div className="text-center">
                    <span className="block text-xl sm:text-2xl font-black text-gold-400">98.5%*</span>
                    <span className="text-[11px] sm:text-xs text-gray-300 font-medium">Satisfaction client</span>
                </div>
                <div className="text-center">
                    <span className="block text-xl sm:text-2xl font-black text-purple-400">24h/24</span>
                    <span className="text-[11px] sm:text-xs text-gray-300 font-medium">Disponibilité directe</span>
                </div>
                <div className="text-center">
                    <span className="block text-xl sm:text-2xl font-black text-gold-400">100%</span>
                    <span className="text-[11px] sm:text-xs text-gray-300 font-medium">Anonymat garanti</span>
                </div>
            </div>
            <p className="text-[10px] text-gray-400/80 italic mt-2.5 text-center">
                *Indice basé sur nos enquêtes internes de suivi qualité et retours d'expérience consultants.
            </p>
        </div>

      </div>
    </section>
  );
};

export default Hero;
